import { useState, useCallback, useEffect } from 'react'
import { createContainer } from 'unstated-next'
import { useRouter } from 'next/router'

import * as api from 'lib/api/pointOfSale'

import { CatalogItem, Order } from 'types/model'
import useLocalStorage from 'lib/hooks/useLocalStorage'
import useIterateUpsells from 'lib/hooks/useIterateUpsells'

type DisplayUpsells = (
  refereneOrder: api.SubmitReferenceOrderRequest,
  upsells: CatalogItem[]
) => void

export interface UpsellContainer {
  isUpsellSubmitting: boolean
  currentUpsell: CatalogItem | undefined
  displayUpsells: DisplayUpsells
  nextUpsell: (yes: boolean) => void
  upsellOrder?: Order
  upsellError?: string
  clearUpsellOrder: () => void
}

const useUpsells = () => {
  const router = useRouter()
  const { current, available, taken, init, next } = useIterateUpsells()

  const [order, setOrder] = useState<api.SubmitReferenceOrderRequest>()
  const [isUpsellSubmitting, setUpsellSubmitting] = useState<boolean>(false)
  const [upsellError, setUpsellError] = useState<string>()
  const [upsellOrder, setUpsellOrder] = useLocalStorage<Order | undefined>(
    'upsellOrder',
    undefined
  )

  const displayUpsells = useCallback(
    (
      referenceOrder: api.SubmitReferenceOrderRequest,
      upsells: CatalogItem[]
    ) => {
      init(upsells)
      setOrder(referenceOrder)
    },
    [init]
  )

  const nextUpsell = useCallback(
    (isTaken: boolean) => {
      next(isTaken)
    },
    [next]
  )

  const clearUpsellOrder = useCallback(() => {
    setUpsellOrder(undefined)
    setUpsellError(undefined)
  }, [setUpsellOrder, setUpsellError])

  // When all upsells have been displayed
  useEffect(() => {
    const getUpsell = (
      items: CatalogItem[],
      values: api.SubmitReferenceOrderRequest
    ) => {
      return {
        ...values,
        enableUpsells: true,
        items: items.map((item, index) => ({
          sku: item.sku,
          stepNumber: index + 2, // Upsell step numbers start at 2
        })),
      }
    }

    const submitUpsell = async () => {
      try {
        if (!order) return

        setUpsellSubmitting(true)
        const res = await api.submitUpsell(getUpsell(taken, order))

        // Handle server error
        if (res.status !== 200) {
          setUpsellError(res.statusText)
          return
        }

        // Handle response error
        const response = await res.data
        if (!response.approved) {
          setUpsellError(response.errorMessage)
          return
        }

        // Save upsell order
        setUpsellOrder({
          customerEmail: order.email,
          orderId: response.order.orderId,
          orderTotal: response.order.orderTotal,
          salesTaxAmount: response.order.salesTaxAmount,
          salesTaxPercent: response.order.salesTaxPercent,
          items: response.order.items,
          currency: response.order.currency,
        })
      } catch (err) {
        if (err instanceof Error) {
          console.log(`Upsell error: ${err.message}`)
          setUpsellError(err.message)
        }
      } finally {
        // Always goto purchase complete regardless of success or failure
        setUpsellSubmitting(false)
        router.push('/complete')
      }
    }

    if (order && available.length === 0) {
      if (taken.length > 0) {
        submitUpsell()
      } else {
        router.push('/complete')
      }
    }
  }, [order, available.length, taken, setUpsellOrder, router])

  return {
    isUpsellSubmitting,
    currentUpsell: current,
    displayUpsells,
    nextUpsell,
    upsellOrder,
    upsellError,
    clearUpsellOrder,
  }
}

export default createContainer<UpsellContainer>(useUpsells)
